/**
 * Authentication Actions
 * Redux actions for authentication and user management
 */

import * as authService from '../../services/authService';
import * as profileService from '../../services/profileService';
import { getAuthToken } from '../../services/apiConfig';

/**
 * Login user
 */
export const loginUser = (credentials) => async (dispatch) => {
  dispatch({ type: 'AUTH_START' });
  try {
    console.log('Login attempt with credentials:', { email: credentials.email || credentials.phone });
    const response = await authService.login(credentials);
    console.log('Login response:', response);
    
    // Extract user and token from response
    // Response structure: { success: true, data: { user: {...}, token: "..." } }
    const user = response.data?.user || response.user || response.data || null;
    const token = response.data?.token || response.token || response.accessToken || getAuthToken();
    
    if (token) {
      dispatch({
        type: 'AUTH_SUCCESS',
        payload: {
          user,
          token,
        },
      });
      return { success: true, data: response };
    } else {
      throw new Error('No token received from server');
    }
  } catch (error) {
    console.error('Login error:', error);
    const errorMessage = error.message || (error.data && (error.data.message || error.data.error)) || 'Login failed';
    dispatch({
      type: 'AUTH_FAILURE',
      payload: errorMessage,
    });
    return { success: false, error: errorMessage };
  }
};

/**
 * Register new user
 */
export const registerUser = (userData) => async (dispatch) => {
  dispatch({ type: 'AUTH_START' });
  try {
    const response = await authService.register(userData);
    
    // Extract user and token from response
    const user = response.data?.user || response.user || response.data || null;
    const token = response.data?.token || response.token || response.accessToken || getAuthToken();
    
    dispatch({
      type: 'AUTH_SUCCESS',
      payload: {
        user,
        token,
      },
    });
    return { success: true, data: response };
  } catch (error) {
    const errorMessage = error.message || (error.data && (error.data.message || error.data.error)) || 'Registration failed';
    dispatch({
      type: 'AUTH_FAILURE',
      payload: errorMessage,
    });
    return { success: false, error: errorMessage };
  }
};

/**
 * Logout user
 */
export const logoutUser = () => async (dispatch) => {
  authService.logout();
  dispatch({ type: 'AUTH_LOGOUT' });
};

/**
 * Get current user profile
 */
export const getCurrentUser = () => async (dispatch) => {
  try {
    const response = await profileService.getProfile();
    // Extract data from response: { success: true, data: {...} }
    const user = response.data || response;
    dispatch({
      type: 'SET_USER',
      payload: user,
    });
    return { success: true, data: user };
  } catch (error) {
    dispatch({
      type: 'AUTH_FAILURE',
      payload: error.message || 'Failed to get user',
    });
    return { success: false, error: error.message };
  }
};

/**
 * Check if user is authenticated (from localStorage)
 */
export const checkAuth = () => async (dispatch) => {
  const token = getAuthToken();
  if (token) {
    // Set loading state while checking
    dispatch({ type: 'AUTH_START' });
    try {
      const response = await profileService.getProfile();
      // Extract data from response: { success: true, data: {...} }
      const user = response.data || response;
      dispatch({
        type: 'AUTH_SUCCESS',
        payload: {
          user,
          token,
        },
      });
    } catch (error) {
      // Token is invalid, clear it
      authService.logout();
      dispatch({ type: 'AUTH_LOGOUT' });
    }
  } else {
    // No token, ensure we're logged out
    dispatch({ type: 'AUTH_LOGOUT' });
  }
};

/**
 * Update user data
 */
export const updateUserData = (userData) => (dispatch) => {
  dispatch({
    type: 'UPDATE_USER',
    payload: userData,
  });
};

/**
 * Update user profile
 */
export const updateProfile = (profileData) => async (dispatch) => {
  dispatch({ type: 'AUTH_START' });
  try {
    const response = await profileService.updateProfile(profileData);
    // Extract data from response: { success: true, data: {...} }
    const user = response.data || response;
    dispatch({
      type: 'SET_USER',
      payload: user,
    });
    dispatch({ type: 'AUTH_SUCCESS', payload: { user, token: getAuthToken() } });
    return { success: true, data: user };
  } catch (error) {
    const errorMessage = error.data?.message || error.message || 'Failed to update profile';
    dispatch({
      type: 'AUTH_FAILURE',
      payload: errorMessage,
    });
    return { success: false, error: errorMessage, message: errorMessage };
  }
};

/**
 * Change user password
 */
export const changePassword = (passwordData) => async (dispatch) => {
  dispatch({ type: 'AUTH_START' });
  try {
    const response = await profileService.changePassword(passwordData);
    return { success: true, data: response };
  } catch (error) {
    const errorMessage = error.data?.message || error.message || 'Failed to change password';
    dispatch({
      type: 'AUTH_FAILURE',
      payload: errorMessage,
    });
    return { success: false, error: errorMessage, message: errorMessage };
  }
};

